<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SupportMessageAuthorType } from '@/types/support'
import type { SupportMessage } from '@/types/support'
import { renderMarkdown } from '@/utils/renderMarkdown'

const props = defineProps<{
  message: SupportMessage
}>()

const { t } = useI18n()

const isOrganizer = computed(() => props.message.author_type === SupportMessageAuthorType.Organizer)

const renderedBody = computed(() => renderMarkdown(props.message.body))

const formattedTime = computed(() =>
  new Date(props.message.created_at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }),
)

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
</script>

<template>
  <div
    class="flex"
    :class="isOrganizer ? 'justify-end' : 'justify-start'"
  >
    <!-- Staff avatar chip -->
    <div
      v-if="!isOrganizer"
      class="shrink-0 w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300 mr-2 mt-1"
      aria-hidden="true"
    >
      S
    </div>

    <div :class="isOrganizer ? 'max-w-[80%] items-end' : 'max-w-[80%] items-start'" class="flex flex-col gap-1">
      <!-- Sender label -->
      <span class="text-xs text-slate-500 px-1">
        {{ isOrganizer ? t('support.thread.author_you') : t('support.thread.author_startline') }}
      </span>

      <!-- Bubble -->
      <div
        class="rounded-2xl px-4 py-3 text-sm"
        :class="isOrganizer
          ? 'bg-emerald-500/10 border border-emerald-500/20 text-slate-200 rounded-tr-sm'
          : 'bg-slate-800 border border-slate-700 text-slate-200 rounded-tl-sm'"
      >
        <!-- Markdown body — rendered through sanitised renderMarkdown -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="prose-support" v-html="renderedBody" />

        <!-- Attachment chips -->
        <div v-if="message.attachments.length > 0" class="mt-3 flex flex-wrap gap-2">
          <a
            v-for="attachment in message.attachments"
            :key="attachment.id"
            :href="attachment.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-700/60 border border-slate-600 text-xs text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
          >
            <svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            <span class="truncate max-w-[160px]">{{ attachment.original_filename }}</span>
            <span class="text-slate-500 shrink-0">{{ formatBytes(attachment.size_bytes) }}</span>
          </a>
        </div>
      </div>

      <!-- Timestamp -->
      <span class="text-xs text-slate-600 px-1">{{ formattedTime }}</span>
    </div>
  </div>
</template>

<style scoped>
.prose-support :deep(p) {
  margin-bottom: 0.5rem;
}
.prose-support :deep(p:last-child) {
  margin-bottom: 0;
}
.prose-support :deep(ul) {
  list-style-type: disc;
  padding-left: 1.25rem;
  margin-bottom: 0.5rem;
}
.prose-support :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.25rem;
  margin-bottom: 0.5rem;
}
.prose-support :deep(strong) {
  font-weight: 700;
  color: rgb(255 255 255);
}
.prose-support :deep(a) {
  color: rgb(52 211 153);
  text-decoration: underline;
}
.prose-support :deep(code) {
  background-color: rgb(30 41 59);
  padding: 0.1rem 0.3rem;
  border-radius: 0.25rem;
  font-size: 0.8em;
}
</style>
